import { TFunction } from 'i18next';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;
const ONE_WEEK = 7 * ONE_DAY;
const EIGHT_DAY = 8 * ONE_DAY;

/**
 * time: seconds
 */
const formatTimeAgo = (time: number, t: TFunction): string => {
  const now = Date.now();
  const after = now - time * 1000;

  if (after < ONE_MINUTE) {
    const seconds = Math.floor(after / ONE_SECOND);
    return t('seconds_ago_plural', { num: seconds }).replace('%d', `${seconds}`);
  }
  if (after < ONE_HOUR) {
    const minutes = Math.floor(after / ONE_MINUTE);
    return t('minutes_ago_plural', { num: minutes }).replace('%d', `${minutes}`);
  }
  if (after < ONE_DAY) {
    const hours = Math.floor(after / ONE_HOUR);
    return t('hours_ago_plural', { num: hours }).replace('%d', `${hours}`);
  }
  if (after < ONE_WEEK) {
    const days = Math.floor(after / ONE_DAY);
    return t('days_ago_plural', { num: days }).replace('%d', `${days}`);
  }
  if (after < EIGHT_DAY) {
    const weeks = Math.floor(after / ONE_WEEK);
    return t('weeks_ago_plural', { num: weeks }).replace('%d', `${weeks}`);
  }

  const timeDayjs = dayjs(time * 1000);
  if (dayjs(now).year() === timeDayjs.year()) {
    return timeDayjs.format('M/D');
  }

  return timeDayjs.format('YYYY/M/D');
};

type UseTimeAgoFormatter = () => (seconds: number) => string;

export const useTimeAgoFormatter: UseTimeAgoFormatter = () => {
  const { t } = useTranslation();

  return useCallback((seconds: number) => formatTimeAgo(seconds, t), [t]);
};
